import React, { useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getServiceImage } from "../../config/serviceImages";
import { SPECIAL_SERVICES_LISTING } from "../../config/pricing";
import { isServiceEnabled } from "../../config";
import CategorySidebar from "./CategorySidebar";
import moveInMoveOutImg from "../../components/images/stage_01/move-in&move-out.png";
import homeUtensilsCleaningImg from "../../components/images/stage_01/home&utensils_Cleaning.png";
import rapidCleaningImg from "../../components/images/stage_01/rapid_cleaning.png";
import deepCleaning169Img from "../../components/images/stage_01/deep_cleaning_169.png";
import kitchenUtensilCleaning169Img from "../../components/images/stage_01/kitchen_utensil_cleaning_169.png";
import rapidCleaning169Img from "../../components/images/stage_01/rapid_cleaning_169.png";
import balconyCleaningImg from "../../components/images/stage_01/balcony_cleaning.png";
import bedroomCleaningImg from "../../components/images/stage_01/bedroom_cleaning.png";

// Explore Other Services — local assets, matching the same category
// thumbnails already used for these cross-links on the other listing pages
// (e.g. TransportServices.js's own extraServices).
import completeHouseCleaningImg from "../../components/images/stage_01/complete_house_cleaning.png";
import electricalImg from "../../components/images/stage_01/electrical.png";
import elderCaretakerImg from "../../components/images/stage_01/elder_caretaker_R2.png";
import carSpaceCleaningImg from "../../components/images/stage_01/car_space_cleaning.png";
import packersMoversImg from "../../components/images/stage_01/complete_packers_movers.png";
import corporateCleaningImg from "../../components/images/stage_01/corporate_cleaning.png";

const fallbackImage =
  "https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=900&q=80";


export const services = [
  {
    id: 1,
    name: "Rapid Home Cleaning",
    desc: "Pre & Post Festival Cleaning — fast, reliable, hassle-free.",
    ...SPECIAL_SERVICES_LISTING[1],
    image: rapidCleaningImg,
    color: "#ef4444",
    link: "/service/special-service/rapid-cleaning"
  },
  {
    id: 2,
    name: "Deep Cleaning",
    desc: "Full intensive cleaning for home, flat, villa or workspace.",
    ...SPECIAL_SERVICES_LISTING[2],
    image: getServiceImage("Full Home Deep Cleaning"),
    color: "#4f46e5",
    link: "/service/special-service/deep-cleaning"
  },
  {
    id: 4,
    name: "Move-in / Move-out Cleaning",
    desc: "Detailed cleaning before shifting in or after vacating property.",
    ...SPECIAL_SERVICES_LISTING[4],
    image: moveInMoveOutImg,
    color: "#22c55e",
    link: "/service/special-service/move-in-move-out-cleaning"
  },
  {
    id: 5,
    name: "Kitchen & Utensil Cleaning",
    desc: "Heavy kitchen degreasing, appliance cleaning and full home utility reset support.",
    ...SPECIAL_SERVICES_LISTING[5],
    image: homeUtensilsCleaningImg,
    color: "#4f46e5",
    link: "/service/special-service/kitchen-utility-cleaning"
  },
  {
    id: 6,
    name: "Balcony, Terrace & Parking Area Cleaning",
    desc: "Pressure washing, algae & moss treatment, garden cleanup and more for balconies & terraces.",
    ...SPECIAL_SERVICES_LISTING[6],
    image: balconyCleaningImg,
    color: "#0d9488",
    link: "/service/special-service/balcony-terrace-cleaning"
  },
  {
    id: 7,
    name: "Bedroom and Wardrobe Cleaning",
    desc: "Dusting, wardrobe organizing, mattress & upholstery care and more for bedrooms & wardrobes.",
    ...SPECIAL_SERVICES_LISTING[7],
    image: bedroomCleaningImg,
    color: "#7c3aed",
    link: "/service/special-service/bedroom-wardrobe-cleaning"
  }
];

const extraServices = [
  {
    name: "Home Services",
    link: "/services/instant-home-services",
    image: completeHouseCleaningImg
  },
  {
    name: "Technical Services",
    link: "/services/technical-services",
    image: electricalImg
  },
  {
    name: "Caretaker Services",
    link: "/services/caretaker-services",
    image: elderCaretakerImg
  },
  {
    name: "Car Space Cleaning",
    link: "/services/outdoor-services",
    image: carSpaceCleaningImg
  },
  {
    name: "Transport Services",
    link: "/services/transport-services",
    image: packersMoversImg
  },
  {
    name: "Corporate Care",
    link: "/services/corporate-services",
    image: corporateCleaningImg
  }
];

const SpecialServices = () => {
  const navigate = useNavigate();
  const galleryScrollRef = useRef(null);

  // A service the Dashboard's Enable/Disable tab turns off is left out of
  // this list entirely, not shown disabled.
  const visibleServices = services.filter((service) => isServiceEnabled(`ss-${service.id}`));

  // "See our professionals at work" — single-row horizontal scroller (not a
  // wrapping grid) so new cards keep landing in the same row; the arrow
  // buttons scroll by one card's width instead of the user needing to
  // trackpad/touch-scroll the row directly.
  const scrollGalleryBy = (direction) => {
    const el = galleryScrollRef.current;
    if (!el) return;
    const card = el.querySelector("[data-gallery-card]");
    const amount = (card?.offsetWidth || 300) + 18; // card width + gap
    el.scrollBy({ left: direction * amount, behavior: "smooth" });
  };

  return (
    <div style={styles.page}>
      <div style={styles.layout}>
        <aside style={styles.sidebar}>
          <CategorySidebar />

          <div style={styles.helpCard}>
            <h4 style={styles.helpTitle}>Need a Special Cleaning Team?</h4>
            <p style={styles.helpText}>We can help with urgent, premium and corporate cleaning requirements.</p>
            <Link to="/contact-us" style={{ ...styles.chatBtn, textDecoration: "none", display: "inline-block" }}>💬 Chat with Us</Link>
            <div style={styles.helpIcon}>✨</div>
          </div>

          <div style={styles.whyCard}>
            <h3 style={styles.whyTitle}>Why Choose Us?</h3>
            <div style={styles.whyList}>
              <div style={styles.whyItem}>Rapid Response Teams</div>
              <div style={styles.whyItem}>Corporate-ready Support</div>
              <div style={styles.whyItem}>Transparent Pricing</div>
              <div style={styles.whyItem}>Premium Cleaning Standards</div>
              <div style={styles.whyItem}>Verified Professionals</div>
              <div style={styles.whyItem}>24/7 Customer Support</div>
            </div>
          </div>
        </aside>

        <main style={styles.main}>
          <div style={styles.breadcrumb}>Home &gt; Special Services</div>

          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>Popular Special Services</h2>
            <span style={styles.serviceCount}>Showing {visibleServices.length} services</span>
          </div>

          <div style={styles.serviceGrid}>
            {visibleServices.map((service) => (
              <div
                key={service.id}
                style={{ ...styles.serviceCard, position: "relative", cursor: "pointer" }}
                onClick={() => navigate(service.link)}
              >
                <img
                  src={service.image}
                  alt={service.name}
                  style={styles.serviceImage}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = fallbackImage;
                  }}
                />

                <div style={styles.serviceBody}>
                  <div
                    style={{
                      ...styles.serviceIcon,
                      background: `${service.color}20`,
                      color: service.color
                    }}
                  >
                    ✦
                  </div>

                  <h3 style={styles.serviceName}>{service.name}</h3>
                  <p style={styles.serviceDesc}>{service.desc}</p>

                  <div style={styles.serviceMeta}>
                    <span style={styles.price}>₹{service.price}</span>
                  </div>

                  <button
                    style={styles.bookBtn}
                    onClick={(e) => { e.stopPropagation(); navigate(service.link); }}
                  >
                    Book Now
                  </button>
                </div>
              </div>
            ))}
          </div>

          <h3 style={styles.gallerySectionTitle}>See our professionals at work</h3>
          <div style={styles.galleryCarouselWrap}>
            <button type="button" style={{ ...styles.galleryArrow, left: "-18px" }} onClick={() => scrollGalleryBy(-1)} aria-label="Previous">
              ‹
            </button>
            <div style={styles.galleryGrid} ref={galleryScrollRef}>
              <div style={styles.galleryCard} data-gallery-card>
                <img src={deepCleaning169Img} alt="Deep cleaning" style={styles.galleryImage}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }} />
                <div style={styles.galleryCaption}>Deep Cleaning</div>
              </div>
              <div style={styles.galleryCard} data-gallery-card>
                <img src={rapidCleaning169Img} alt="Rapid Home Cleaning" style={styles.galleryImage}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }} />
                <div style={styles.galleryCaption}>Rapid Home Cleaning</div>
              </div>
              <div style={styles.galleryCard} data-gallery-card>
                <img src={kitchenUtensilCleaning169Img} alt="Kitchen and utensils Cleaning" style={styles.galleryImage}
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }} />
                <div style={styles.galleryCaption}>Kitchen and utensils Cleaning</div>
              </div>
            </div>
            <button type="button" style={{ ...styles.galleryArrow, right: "-18px" }} onClick={() => scrollGalleryBy(1)} aria-label="Next">
              ›
            </button>
          </div>

          <div style={styles.exploreSection}>
            <h3 style={styles.exploreTitle}>Explore Other Services</h3>

            <div style={styles.exploreGrid}>
              {extraServices.map((item, index) => (
                <Link key={index} to={item.link} style={styles.exploreCardLink}>
                  <div style={styles.exploreCard}>
                    <img
                      src={item.image}
                      alt={item.name}
                      style={styles.exploreImage}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = fallbackImage;
                      }}
                    />
                    <div style={styles.exploreCardBody}>
                      <h4 style={styles.exploreName}>{item.name}</h4>
                      <span style={styles.exploreLink}>Check Now →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Banner — Silver (membership subscription upsell, same pattern
              as OutdoorServices.js's Bronze Package / TransportServicesComingSoon.js's
              Gold Package / CorporateServices.js's Platinum banners — cool
              steel/silver accent for the "silver" metal theme). */}
          <div style={styles.bannerCard}>
            <img
              src={homeUtensilsCleaningImg}
              alt="Silver"
              style={styles.bannerImage}
              onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = fallbackImage; }}
            />
            <div style={styles.bannerContent}>
              <span style={styles.bannerBadge}>Subscription Plan</span>
              <h3 style={styles.bannerTitle}>Silver</h3>
              <p style={styles.bannerText}>26 visits every month, 2 hrs 30 min per visit</p>
              <ul style={styles.bannerList}>
                <li style={styles.bannerListItem}><span style={styles.bannerCheckIcon}>✓</span> 26 Service Visits / Month (2 hrs 30 min each)</li>
                <li style={styles.bannerListItem}><span style={styles.bannerCheckIcon}>✓</span> 2 Partners — Flexible Slot Timings</li>
                <li style={styles.bannerListItem}><span style={styles.bannerCheckIcon}>✓</span> Choose 1, 3, 6 or 12 Month Plans</li>
                <li style={styles.bannerListItem}><span style={styles.bannerCheckIcon}>✓</span> Free Product Kit on Every Tier</li>
                <li style={styles.bannerListItem}><span style={styles.bannerCheckIcon}>✓</span> Max Discount on Services</li>
              </ul>
            </div>
            <div style={styles.bannerAction}>
              <p style={styles.saveText}>Flexible Monthly Plans</p>
              <Link to="/subscription-plans/checkout/platinum-plus-package" style={styles.bookNowBtnLink}>Book Now →</Link>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const styles = {
  page: {
    background: "#f8fafc",
    minHeight: "100vh",
    padding: "20px",
    fontFamily: "Arial, sans-serif",
    color: "#111827"
  },
  layout: {
    maxWidth: "1600px",
    margin: "0 auto",
    display: "grid",
    gridTemplateColumns: "250px 1fr",
    gap: "20px",
    alignItems: "start"
  },
  sidebar: {
    display: "flex",
    flexDirection: "column",
    gap: "18px"
  },
  helpCard: {
    background: "#f5f3ff",
    border: "1px solid #ddd6fe",
    borderRadius: "18px",
    padding: "18px",
    textAlign: "left"
  },
  helpTitle: {
    margin: "0 0 8px",
    fontSize: "18px",
    fontWeight: 700
  },
  helpText: {
    margin: "0 0 14px",
    fontSize: "14px",
    color: "#6b7280",
    lineHeight: 1.6
  },
  chatBtn: {
    border: "none",
    background: "#fff",
    color: "#4f46e5",
    fontWeight: 700,
    borderRadius: "12px",
    padding: "12px 16px",
    cursor: "pointer"
  },
  helpIcon: {
    fontSize: "54px",
    textAlign: "center",
    marginTop: "18px"
  },
  main: {
    minWidth: 0
  },
  breadcrumb: {
    fontSize: "13px",
    color: "#9ca3af",
    marginBottom: "14px"
  },
  sectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px"
  },
  sectionTitle: {
    margin: 0,
    fontSize: "24px",
    fontWeight: 700
  },
  serviceCount: {
    fontSize: "14px",
    color: "#6b7280"
  },
  serviceGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: "14px"
  },
  serviceCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "14px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    height: "100%"
  },
  serviceImage: {
    width: "100%",
    aspectRatio: "1 / 1",
    objectFit: "cover"
  },
  serviceBody: {
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    flex: 1
  },
  serviceIcon: {
    width: "26px",
    height: "26px",
    borderRadius: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "8px",
    fontSize: "13px",
    fontWeight: 700
  },
  serviceName: {
    margin: "0 0 6px",
    fontSize: "15px",
    fontWeight: 700
  },
  serviceDesc: {
    margin: "0 0 10px",
    fontSize: "12px",
    lineHeight: 1.5,
    color: "#6b7280",
    minHeight: "48px"
  },
  serviceMeta: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "auto",
    marginBottom: "10px"
  },
  price: {
    fontSize: "17px",
    fontWeight: 800
  },
  bookBtn: {
    width: "100%",
    height: "36px",
    border: "none",
    background: "linear-gradient(135deg,#4f46e5,#4338ca)",
    color: "#fff",
    borderRadius: "10px",
    fontWeight: 700,
    fontSize: "12.5px",
    cursor: "pointer"
  },
  gallerySectionTitle: { margin: "32px 0 16px", fontSize: "24px", fontWeight: 800 },
  galleryCarouselWrap: { position: "relative", padding: "0 6px" },
  galleryGrid: {
    display: "flex",
    gap: "18px",
    overflowX: "auto",
    scrollSnapType: "x mandatory",
    scrollBehavior: "smooth",
    paddingBottom: "4px",
  },
  galleryCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "18px",
    overflow: "hidden",
    flex: "0 0 300px",
    width: "300px",
    scrollSnapAlign: "start",
  },
  galleryImage: { width: "100%", aspectRatio: "16 / 9", objectFit: "cover", display: "block" },
  galleryCaption: { padding: "14px 16px", fontWeight: 700, fontSize: "15px" },
  galleryArrow: {
    position: "absolute",
    top: "calc(50% - 36px)",
    transform: "translateY(-50%)",
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    border: "1px solid #e5e7eb",
    background: "#fff",
    color: "#111827",
    fontSize: "20px",
    fontWeight: 700,
    lineHeight: 1,
    cursor: "pointer",
    boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
  },
  exploreSection: {
    marginTop: "24px"
  },
  exploreTitle: {
    margin: "0 0 14px",
    fontSize: "24px",
    fontWeight: 700
  },
  exploreGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    gap: "12px"
  },
  exploreCardLink: {
    textDecoration: "none",
    color: "inherit"
  },
  exploreCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "16px",
    overflow: "hidden",
    textAlign: "left"
  },
  exploreImage: {
    width: "100%",
    aspectRatio: "1 / 1",
    objectFit: "cover",
    display: "block",
    background: "#f3f4f6"
  },
  exploreCardBody: {
    padding: "14px"
  },
  exploreName: {
    margin: "0 0 10px",
    fontSize: "15px",
    fontWeight: 700
  },
  exploreLink: {
    color: "#4f46e5",
    fontWeight: 700,
    fontSize: "13px"
  },
  // Silver banner — same gradient-card pattern as OutdoorServices.js's
  // Bronze Package / TransportServicesComingSoon.js's Gold Package /
  // CorporateServices.js's Platinum banners, cool steel/grey for "silver".
  bannerCard: {
    marginTop: "32px",
    background: "linear-gradient(135deg, #94a3b8 0%, #475569 100%)",
    borderRadius: "20px",
    padding: "22px",
    display: "grid",
    gridTemplateColumns: "220px 1fr auto",
    gap: "24px",
    alignItems: "center",
    boxShadow: "0 14px 34px rgba(71,85,105,0.3)"
  },
  bannerImage: {
    width: "100%",
    aspectRatio: "4 / 3",
    objectFit: "cover",
    borderRadius: "16px",
    display: "block",
    boxShadow: "0 6px 18px rgba(0,0,0,0.25)"
  },
  bannerContent: {},
  bannerBadge: {
    display: "inline-block",
    background: "rgba(255,255,255,0.22)",
    color: "#fff",
    fontSize: "11px",
    fontWeight: 800,
    letterSpacing: "0.6px",
    textTransform: "uppercase",
    padding: "5px 12px",
    borderRadius: "999px",
    marginBottom: "10px"
  },
  bannerTitle: {
    margin: "0 0 6px",
    fontSize: "26px",
    fontWeight: 800,
    color: "#fff"
  },
  bannerText: {
    margin: "0 0 14px",
    fontSize: "14px",
    color: "rgba(255,255,255,0.9)"
  },
  bannerList: {
    margin: 0,
    padding: 0,
    listStyle: "none",
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  bannerListItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    fontSize: "13.5px",
    fontWeight: 600,
    color: "#fff"
  },
  bannerCheckIcon: {
    color: "#166534",
    fontWeight: 900,
    flexShrink: 0
  },
  bannerAction: {
    textAlign: "center"
  },
  saveText: {
    margin: "0 0 12px",
    fontSize: "12px",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "rgba(255,255,255,0.85)"
  },
  bookNowBtnLink: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "none",
    background: "#fff",
    color: "#334155",
    height: "50px",
    padding: "0 26px",
    borderRadius: "12px",
    fontWeight: 800,
    fontSize: "14.5px",
    cursor: "pointer",
    textDecoration: "none",
    whiteSpace: "nowrap",
    boxShadow: "0 6px 16px rgba(0,0,0,0.2)"
  },
  whyCard: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "18px",
    padding: "18px"
  },
  whyTitle: {
    margin: "0 0 16px",
    fontSize: "22px",
    fontWeight: 700
  },
  whyList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px"
  },
  whyItem: {
    background: "#fafafa",
    border: "1px solid #f1f5f9",
    borderRadius: "12px",
    padding: "12px 14px",
    fontSize: "14px",
    fontWeight: 600,
    color: "#374151"
  }
};

export default SpecialServices;